"use client";

import { ChevronRight, Triangle } from "lucide-react";

import {
  SITTING_WINDOW_HOURS,
  useSittingActivity,
} from "../_lib/use-sitting-activity";
import { LiveSittingsChart } from "./live-sittings-chart";

interface LiveSittingsPanelProps {
  slug: string;
}

/**
 * Live sitting activity for one assessment: the counts, and the hourly series
 * below them.
 *
 * A client component on a page that is prerendered with ISR. The static HTML
 * ships without these figures and the panel fills them in on mount, which is the
 * only way a five-minute-old page can say "9 sitting now" and be right.
 *
 * Every colour is a marketing token, none literal, so the panel follows the
 * light/dark flip that `.dark .assessments-surface` applies to this page. The
 * design's hexes happen to be exactly those tokens' light values: #1a1a1a,
 * #606060, #a19d92 on the chevron, and the green badge trio.
 */
export function LiveSittingsPanel({ slug }: LiveSittingsPanelProps) {
  const { data, isPending } = useSittingActivity(slug);
  const total = data?.total ?? 0;

  return (
    <div className="border-marketing-blue/20 bg-marketing-surface relative h-[196px] w-full overflow-hidden rounded-[14px] border">
      {/* The hue: a blue wash off the top edge, kept faint so the card still
          reads as white. Behind the content and not interactive. */}
      <div
        aria-hidden
        className="from-marketing-blue/[0.04] pointer-events-none absolute inset-x-0 top-0 h-2/5 bg-gradient-to-b to-transparent"
      />

      <div className="relative flex items-center gap-2 px-5 pt-5">
        <span className="text-marketing-text text-[15px] font-semibold tracking-[-0.006em]">
          Assessment
        </span>
        <span className="text-marketing-text-muted text-[15px]">
          {SITTING_WINDOW_HOURS}h
        </span>
        <ChevronRight
          aria-hidden
          className="text-marketing-text-secondary ml-auto size-3.5"
          strokeWidth={1.7}
        />
      </div>

      <div className="relative mt-2 flex items-center gap-2.5 px-5">
        <span className="text-marketing-text text-[17px] font-semibold tracking-[-0.008em]">
          {isPending ? "-" : `${total} ${total === 1 ? "sitting" : "sittings"}`}
        </span>
        {data && data.today > 0 ? (
          <span className="border-marketing-badge-green-border bg-marketing-badge-green-bg text-marketing-accent-green-text flex items-center gap-1.5 rounded-full border px-2 py-0.5">
            <Triangle
              aria-hidden
              className="size-2 fill-current"
              strokeWidth={0}
            />
            <span className="font-mono text-[11.5px] font-medium">
              {data.today} today
            </span>
          </span>
        ) : null}
      </div>

      <div className="relative mt-2 flex items-center gap-2 px-5">
        <span
          aria-hidden
          className="bg-marketing-blue size-2 shrink-0 rounded-full"
        />
        <span className="text-marketing-text text-[14px]">
          {isPending
            ? "Loading activity"
            : `${data?.sittingNow ?? 0} sitting now`}
        </span>
      </div>

      {/* Pinned to the bottom edge with no padding: the area fill is meant to run
          into the panel's corner, so the plot owns the full width. */}
      <div className="absolute bottom-0 left-0 h-[88px] w-full">
        {data ? <LiveSittingsChart buckets={data.buckets} /> : null}
      </div>
    </div>
  );
}
